/**
 * Global network status — connectivity + "this is taking a while".
 *
 * Two independent signals, both surfaced as a thin reactive layer the
 * UI subscribes to:
 *
 *  - `isOnline` — mirrors `navigator.onLine` and the online/offline
 *    events. Drives the offline banner.
 *  - `isSlow` — true while at least one request has been in flight
 *    longer than `SLOW_AFTER_MS`. The api client reports request
 *    start/end via `markRequestStart` / `markRequestEnd`; we keep an
 *    in-flight count and arm a timer on the first concurrent request.
 *    Drives the "slow connection" hint.
 *
 * Kept framework-light (module-level refs, no Pinia) because it's pure
 * cross-cutting transport state with a single source of truth — the
 * same reason the api client is a singleton. Tests can reset it via
 * `_resetNetwork()`.
 */
import { readonly, ref } from 'vue'

/** How long a request may run before we call the connection "slow". */
export const SLOW_AFTER_MS = 5_000

const online = ref(typeof navigator === 'undefined' ? true : navigator.onLine)
const slow = ref(false)

/** Reactive: is the browser online? */
export const isOnline = readonly(online)
/** Reactive: is at least one request running longer than the threshold? */
export const isSlow = readonly(slow)

let inFlight = 0
let slowTimer: ReturnType<typeof setTimeout> | null = null

function armSlowTimer() {
  if (slowTimer !== null) return
  slowTimer = setTimeout(() => {
    // Only flip to slow if something is still actually pending.
    if (inFlight > 0) slow.value = true
    slowTimer = null
  }, SLOW_AFTER_MS)
}

function disarmSlowTimer() {
  if (slowTimer !== null) {
    clearTimeout(slowTimer)
    slowTimer = null
  }
}

/** Called by the api client when a request begins. */
export function markRequestStart(): void {
  inFlight += 1
  if (inFlight === 1) armSlowTimer()
}

/** Called by the api client when a request settles (success or failure). */
export function markRequestEnd(): void {
  inFlight = Math.max(0, inFlight - 1)
  if (inFlight === 0) {
    disarmSlowTimer()
    slow.value = false
  }
}

function setOnline(v: boolean) {
  online.value = v
}

// Named handlers + a watching flag so the listeners are genuinely
// idempotent (no duplicate bindings on a second call) and removable
// (so `_resetNetwork` doesn't leak them between tests).
const onOnline = () => setOnline(true)
const onOffline = () => setOnline(false)
let watching = false

/**
 * Bind to the browser's connectivity events. Idempotent; call once at
 * app start. No-op outside the browser (SSR / unit tests).
 */
export function startNetworkWatch(): void {
  if (typeof window === 'undefined' || watching) return
  watching = true
  online.value = navigator.onLine
  window.addEventListener('online', onOnline)
  window.addEventListener('offline', onOffline)
}

/** Detach the connectivity listeners. Pairs with `startNetworkWatch`. */
export function stopNetworkWatch(): void {
  if (typeof window === 'undefined' || !watching) return
  watching = false
  window.removeEventListener('online', onOnline)
  window.removeEventListener('offline', onOffline)
}

/** Test helper — reset all state to a clean baseline. */
export function _resetNetwork(): void {
  disarmSlowTimer()
  stopNetworkWatch()
  inFlight = 0
  slow.value = false
  online.value = typeof navigator === 'undefined' ? true : navigator.onLine
}
